import React, { useMemo, useState, useEffect } from "react";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import ReportPageAction from "../page-action/ReportPageAction";
import LtvFilter from "./LtvFilter";
import LtvReportTable from "../../../components/table/all-tables/reportable/ltvreport/LtvReportTable";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  ltvReport,
  buttonName,
  userReports,
  reffPartner
} from "../../../locales/en";
import { showToast } from "../../../utils/utils";
import {
  MONTH_12,
  showToastType,
  VIEW_OPTIONS_TYPE
} from "../../../utils/constants/appConstants";
import LTVReportDownloadModal from "../../../components/modals/LTVReportDownloadModal";
import ".././reportStyle.scss";
import { getLTVReportThunk } from "../../../redux/userReportsSlice";
import { useDispatch, useSelector } from "react-redux";
import CircleLoader from "../../../components/common/CircleLoader";
import "./ltvGraph.scss";
import ErrorBoundary from "../../../components/error-boundary";
import ComponentViewFallback from "../../../components/error-boundary/fallback/ComponentViewFallback";
import { PERIOD_BY_VIEWTYPE } from "../../../utils/getDropdownOptions";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function LTVReport() {
  const dispatch = useDispatch();
  const [data, setData] = useState({});
  const [addReportDownload, setAddReportDownload] = useState(false);
  const toggleReportDownload = () => {
    setAddReportDownload(!addReportDownload);
  };
  const { LTVloading } = useSelector((state) => state.userReport);

  const initialLtvFilter = {
    viewType: VIEW_OPTIONS_TYPE?.MONTH,
    salesLeadName: "",
    period: MONTH_12,
    country: "",
    customerDuration: "",
    referralType: "",
    referralSubType: "",
    referralPartnerName: "",
    asAtDate: new Date().toISOString()
  };
  const [ltvFilter, setLtvFilter] = useState(initialLtvFilter);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLtvFilter((prev) => {
      if (name === "viewType") {
        return {
          ...prev,
          viewType: value,
          period: PERIOD_BY_VIEWTYPE[value] ?? ""
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const fetchLtvReport = async (filters) => {
    try {
      const response = await dispatch(getLTVReportThunk(filters)).unwrap();
      setData(response.data.data);
    } catch {
      showToast(showToastType.error);
    }
  };

  const rowLabels = useMemo(() => {
    const matrix = Array.isArray(data?.data) ? data.data : [];
    const averageRegion = matrix?.[2];
    if (!Array.isArray(averageRegion)) return [];
    return averageRegion
      .map(([label]) => label)
      .filter((label) => label !== "TOTAL");
  }, [data]);

  const chartData = useMemo(() => {
    const matrix = Array.isArray(data?.data) ? data.data : [];
    const averageRegion = matrix?.[3];
    if (!Array.isArray(averageRegion)) return { labels: [], datasets: [] };

    const values = averageRegion
      .filter(([label]) => label !== "TOTAL")
      .map(([, cell]) => {
        const raw = cell?.value?.replace(/[$,]/g, "") || "0";
        return parseFloat(raw);
      });

    return {
      labels: rowLabels,
      datasets: [
        {
          label: ltvFilter.viewType,
          data: values,
          borderWidth: 1,
          backgroundColor: "#4cc9f5",
          borderColor: "#4cc9f5",
          pointBackgroundColor: "#d71016"
        }
      ]
    };
  }, [data, ltvFilter.viewType, rowLabels]);

  const options = useMemo(
    () => ({
      responsive: true,
      interaction: {
        mode: "index",
        intersect: false
      },
      stacked: false,
      plugins: {
        title: {
          display: true,
          text: userReports?.ltvReport?.text1
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const value = context.raw;
              return `${ltvFilter.viewType}: $${value.toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }
              )}`;
            }
          }
        }
      },
      scales: {
        y: {
          type: "linear",
          display: true,
          position: "left",
          beginAtZero: true,
          title: {
            display: true,
            text: reffPartner?.monthlyCommReport?.commisonModal?.th5
          },
          ticks: {
            callback: function (value) {
              return `$${value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
            }
          }
        },
        x: {
          title: {
            display: true,
            text: userReports?.ltvReport?.text2
          }
        }
      },
      elements: {
        line: {
          borderWidth: 1
        },
        point: {
          borderWidth: 1
        }
      }
    }),
    [ltvFilter.viewType]
  );

  useEffect(() => {
    fetchLtvReport(ltvFilter);
  }, [ltvFilter]);

  return (
    <>
      <TablePagesTitle mainTitle={ltvReport?.mainTitle} />

      <ReportPageAction subheading={ltvReport?.mainTitle} />

      <div className="d-flex flex-wrap mt-4 mb-3 filter full_filter justify-content-between search">
        <LtvFilter
          LTVloading={LTVloading}
          ltvFilter={ltvFilter}
          onInputChange={handleInputChange}
        />
        <button
          disabled={LTVloading}
          onClick={toggleReportDownload}
          className="btn btn-primary"
        >
          {buttonName?.download}
        </button>
      </div>
      <div className="row my-4 py-4 px-0 mx-0">
        <div className="col-md-12 px-0">
          {LTVloading ? (
            <CircleLoader />
          ) : (
            <ErrorBoundary fallback={<ComponentViewFallback />}>
              <LtvReportTable
                data={Array.isArray(data?.data) ? data.data : []}
              />
            </ErrorBoundary>
          )}
        </div>
        <div className="col-md-12 px-0">
          <div className={`graphSec w-100 ${LTVloading ? "makeBlur" : ""}`}>
            <Line options={options} data={chartData} />
            {LTVloading && <span className="loading-text" />}
          </div>
        </div>
      </div>
      <LTVReportDownloadModal
        isOpen={addReportDownload}
        toggle={toggleReportDownload}
      />
    </>
  );
}
